import {
  Box,
  Button,
  Card,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  TextareaAutosize,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useContext, useEffect, useState } from "react";
import { AuthContext } from "../../../../context/AuthContext";
import WorkOrderButton from "../../../../components/PdfButtons/WorkOrderButton";
import OrderItemDialogButton from "./OrderItems/OrderItemDialogButton";
import { Close, Delete, Edit, UnfoldMore } from "@mui/icons-material";

import dayjs from "dayjs";
import utc from "dayjs/plugin/utc"; // ES 2015
import timezone from "dayjs/plugin/timezone"; // ES 2015
import isoWeek from "dayjs/plugin/isoWeek"; // ES 2015
import getAvatarColor from "../../../../helper/getAvatarColor";
import { LoadingButton } from "@mui/lab";
import FabricatorNotes from "../../../shop/Orders/FabricatorNotes";
import RFLTaskDialogButton from "./RFLTaskDialogButton";
import { useNavigate } from "react-router-dom";
import Tiptap from "../../../../components/Tip/Tiptap";
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(isoWeek);
const DEFAULT_TIMEZONE = "America/Phoenix";
dayjs.tz.setDefault(DEFAULT_TIMEZONE);

const JobDialog = ({
  dialogData,
  handleClose,
  handleEdit,
  handleDelete,
  handleCopyUnfinished,
  handleSubmit,
  errorMessage,
}) => {
  const { authToken, ezorder, decoded } = useContext(AuthContext);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const [calendarEvent, setCalendarEvent] = useState(null);
  const [eventPhases, setEventPhases] = useState([]);
  const [productMap, setProductMap] = useState(new Map());
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [fabricatorNote, setFabricatorNote] = useState("");
  const [shopNotes, setShopNotes] = useState([]);
  const [isLoadingNotes, setIsLoadingNotes] = useState(false);
  const [updateProgressApiError, setUpdateProgressApiError] = useState("");
  const [updatingProgress, setUpdatingProgress] = useState(false);
  const navigate = useNavigate();

  const getEventDetails = async (eventId) => {
    try {
      setLoading(true);
      const res = await ezorder.get(`/shop/schedule/events/${eventId}`, {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });
      let event = res.data.calendarEvent;
      setCalendarEvent(event);

      // TODO: parse the event subphases and group into phases
      console.log("SubPhases", event.subPhases);
      let phaseMap = new Map();
      for (let subPhase of event.subPhases) {
        console.log("subPhase", subPhase);
        if (phaseMap.get(subPhase.phase.name)) {
          let phase = phaseMap.get(subPhase.phase.name);
          phase.subPhases = [...phase.subPhases, subPhase];
          phaseMap.set(subPhase.phase.name, phase);
        } else {
          phaseMap.set(subPhase.phase.name, {
            ...subPhase.phase,
            subPhases: [subPhase],
          });
        }
      }
      console.log("phaseMap", phaseMap);
      // Get Array of Phases
      setEventPhases(Array.from(phaseMap.values()));

      if (event.order) {
        const prodMap = new Map();
        for (let i = 0; i < event.order.items.length; i++) {
          let orderItem = event.order.items[i];
          prodMap.set(`${orderItem.objectType}`, orderItem);
        }
        setProductMap(prodMap);
      }
    } catch (error) {
      console.log(error);
      // setError(error);
    } finally {
      setLoading(false);
    }
  };

  const updateSubPhase = async (subPhaseId, progress) => {
    try {
      setUpdating(true);
      const res = await ezorder.put(
        // `/shop/orders/subPhase/${subPhaseId}`,
        `/shop/schedule/events/${dialogData.calendarEvent.id}/subPhase/${subPhaseId}`,
        { progress },
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        },
      );
      getEventDetails(dialogData.calendarEvent.id);
    } catch (error) {
      console.log(error);
      // setError(error);
    } finally {
      setUpdating(false);
    }
  };

  const updateTaskProgress = async (rflTaskId, progress, materials) => {
    try {
      setUpdateProgressApiError("");
      setUpdatingProgress(true);
      const response = await ezorder.put(
        `/shop/schedule/events/${dialogData.calendarEvent.id}/rflTask/${rflTaskId}`,
        { progress, materials },
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        },
      );

      if (response.status == 200 || response.status === 201) {
        getEventDetails(dialogData.calendarEvent.id);
      } else {
        setUpdateProgressApiError(response.response.data.error);
      }
    } catch (error) {
      console.log(error);
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        console.log(error.response.data);
        console.log(error.response.status);
        console.log(error.response.headers);
        // openSnackMessage("error", error.response.data.error);
        setUpdateProgressApiError(error.response.data.error);
      } else {
        // Something went really bad
        console.log(error);
        // openSnackMessage("error", error);
        setUpdateProgressApiError(error);
      }
    } finally {
      setUpdatingProgress(false);
    }
  };

  const getShopNotes = async (orderId) => {
    try {
      setIsLoadingNotes(true);
      let data = {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      };

      // if (controller) {
      //   data.signal = controller.signal;
      // }

      // Internal notes
      let url = `/shop/orders/${orderId}/shop-notes`;

      const response = await ezorder.get(url, data);
      setShopNotes(response.data.shopNotes);
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoadingNotes(false);
    }
  };

  const addShopNote = async (fabricatorNote, isInternal) => {
    try {
      setIsLoadingNotes(true);

      let requestBody = {
        shopNotes: fabricatorNote,
        isInternal,
      };

      const response = await ezorder.post(
        `/shop/orders/${calendarEvent.order.id}/shop-notes`,
        requestBody,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        },
      );
      if (response.status == 200 || response.status === 201) {
        getShopNotes(calendarEvent.order.id);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoadingNotes(false);
    }
  };

  useEffect(() => {
    if (dialogData.open && dialogData.calendarEvent) {
      // TODO: get jobPhases
      getEventDetails(dialogData.calendarEvent.id);
    } else {
      setEventPhases([]);
    }
  }, [dialogData]);

  useEffect(() => {
    if (calendarEvent && calendarEvent.order) {
      getShopNotes(calendarEvent.order.id);
    } else {
      setShopNotes([]);
    }
  }, [calendarEvent]);

  if (dialogData)
    return (
      <Dialog
        open={dialogData.open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth={true}
        fullScreen={isSmallScreen}
      >
        {calendarEvent && (
          <>
            <DialogTitle sx={{ display: "flex", flexDirection: "row" }}>
              {calendarEvent.eventEntity == "IND" ? (
                <Box sx={{ flex: 1 }}>
                  {calendarEvent.order &&
                    calendarEvent.order.company &&
                    calendarEvent.order.project &&
                    calendarEvent.order.orderStatus && (
                      <>
                        IND Job #{calendarEvent.order.id} -{" "}
                        {calendarEvent.order.company.name}
                        <DialogContentText sx={{ display: "block" }}>
                          {calendarEvent.order.company.isVendorOrSupplier
                            ? "Vendor/Supplier Customer"
                            : "Project"}
                          : {calendarEvent.order.project.name}
                        </DialogContentText>
                        {calendarEvent.order.vendorProjectName && (
                          <DialogContentText sx={{ display: "block" }}>
                            Project: {calendarEvent.order.vendorProjectName}
                          </DialogContentText>
                        )}
                        {calendarEvent.order.subProject && (
                          <DialogContentText sx={{ display: "block" }}>
                            Sub-Project: {calendarEvent.order.subProject}
                          </DialogContentText>
                        )}
                        <DialogContentText sx={{ display: "block" }}>
                          {calendarEvent.order.poNumber
                            ? `PO#: ${calendarEvent.order.poNumber}`
                            : ""}
                          <Button
                            sx={{ padding: 0, margin: 0 }}
                            onClick={() => {
                              navigate(`/orders/${calendarEvent.order.id}`);
                            }}
                          >
                            View Order
                          </Button>
                        </DialogContentText>
                      </>
                    )}
                </Box>
              ) : (
                <Box sx={{ flex: 1 }}>
                  {calendarEvent.rflWorkOrder
                    ? `RFL Work Order #${calendarEvent.rflWorkOrder.id}`
                    : `RFL Mfg. Order #${calendarEvent.manufacturingOrderId}`}
                </Box>
              )}
              {decoded.role == "EZORDER_ADMIN" && (
                <Box>
                  <IconButton onClick={handleDelete} sx={{}}>
                    <Delete />
                  </IconButton>
                </Box>
              )}
              {decoded.role == "EZORDER_ADMIN" && (
                <Box>
                  <IconButton
                    onClick={() => {
                      handleEdit(calendarEvent);
                    }}
                    sx={{}}
                  >
                    <Edit />
                  </IconButton>
                </Box>
              )}
              <Box>
                <IconButton onClick={handleClose} sx={{}}>
                  <Close />
                </IconButton>
              </Box>
            </DialogTitle>

            {loading ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minHeight: "200px",
                }}
              >
                <CircularProgress size="3rem" />
              </Box>
            ) : (
              <>
                {calendarEvent && (
                  <DialogContent>
                    <Divider sx={{ marginBottom: "10px" }} />
                    <DialogContentText>
                      Work Station:{" "}
                      {calendarEvent.workStation
                        ? calendarEvent.workStation.name
                        : ""}
                    </DialogContentText>

                    <DialogContentText>
                      Date:{" "}
                      {dayjs(calendarEvent.startDate)
                        .tz("America/Phoenix")
                        .format("MM/DD/YYYY")}{" "}
                    </DialogContentText>
                    <DialogContentText>
                      Time:{" "}
                      {dayjs(calendarEvent.startDate)
                        .tz("America/Phoenix")
                        .format("h:mm a")}
                      {` - `}
                      {dayjs(calendarEvent.endDate)
                        .tz("America/Phoenix")
                        .format("h:mm a")}
                    </DialogContentText>
                    {/* <DialogContentText>
                      Assigned To:{" "}
                      {calendarEvent.assignedTo
                        .map((u) => u.fullName)
                        .join(", ")}
                    </DialogContentText> */}
                    <DialogContentText>Assigned To:</DialogContentText>
                    <Box sx={{ paddingLeft: "16px" }}>
                      {calendarEvent.assignedTo.map((user, index) => {
                        return (
                          <Box
                            key={user.id}
                            sx={{
                              display: "flex",
                              flexDirection: "row",
                              alignItems: "center",
                            }}
                          >
                            <Box
                              sx={{
                                // borderRadius: "50%",
                                // width: "16px",
                                // height: "16px",
                                width: "16px",
                                height: "16px",

                                background: getAvatarColor(user.id),
                              }}
                            />
                            <DialogContentText
                              sx={{ flex: 1, marginLeft: "8px" }}
                            >
                              {user.fullName}
                            </DialogContentText>
                          </Box>
                        );
                      })}
                    </Box>

                    <Divider sx={{ marginY: "10px" }} />
                    <Box sx={{ width: "100%" }}>
                      <Typography variant="h6">Tasks</Typography>
                      {/* Event Description*/}
                      {calendarEvent.eventEntity == "IND" && (
                        <Box
                          style={{
                            flex: 1,
                            // paddingLeft: 16, paddingRight: 16
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "row",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <TextareaAutosize
                              // placeholder="Add instructions or end of day progress notes here"
                              style={{
                                marginTop: "8px",
                                // color: "#fff",
                                flex: 1,
                              }}
                              fullWidth
                              minRows={3}
                              value={calendarEvent.eventNotes}
                              onChange={(e) => {
                                // setFabricatorNote(e.target.value);
                              }}
                            />
                          </Box>
                        </Box>
                      )}

                      {/* Phases */}
                      {/* {updating ? (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            minHeight: "200px",
                          }}
                        >
                          <CircularProgress size="3rem" />
                        </Box>
                      ) : (
                        <>
                          <Box>
                            <Box>
                              {productMap &&
                                eventPhases &&
                                eventPhases.map((phase) => {
                                  let progress = 0;
                                  for (let subphase of phase.subPhases) {
                                    progress += subphase.progress;
                                  }

                                  progress = progress / phase.subPhases.length;

                                  return (
                                    <Box>
                                      <OrderItemDialogButton
                                        order={calendarEvent.order}
                                        productMap={productMap}
                                        productType={phase.name}
                                        phase={phase}
                                        updateSubPhase={updateSubPhase}
                                      >
                                        <Box
                                          sx={{
                                            display: "flex",
                                            flexDirection: "row",
                                            justifyContent: "center",
                                            alignItems: "center",
                                            padding: "16px",
                                            cursor: "pointer",
                                            "&:hover": {
                                              background: "#eeeeee",
                                            },
                                          }}
                                        >
                                          <Box sx={{ flex: 1 }}>
                                            <Typography>
                                              {phase.name}
                                            </Typography>
                                          </Box>
                                          <Typography
                                            sx={{
                                              fontWeight: "bold",
                                              color:
                                                progress == 100
                                                  ? "green"
                                                  : "orange",
                                            }}
                                          >
                                            {progress}%
                                          </Typography>
                                        </Box>
                                      </OrderItemDialogButton>
                                      <Divider />
                                    </Box>
                                  );
                                })}
                            </Box>
                          </Box>
                        </>
                      )} */}
                      {/* RFL Transactions and Tasks */}
                      {updating ? (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            minHeight: "200px",
                          }}
                        >
                          <CircularProgress size="3rem" />
                        </Box>
                      ) : (
                        // IS AN RFL JOB - Old version uses rflTasks, new version usesrflWorkOrder
                        <>
                          {calendarEvent.rflWorkOrder ? (
                            <Box>
                              <Typography sx={{ marginTop: "16px" }}>
                                Subject: {calendarEvent.rflWorkOrder.subject}
                              </Typography>
                              <Tiptap
                                editable={false}
                                editorContent={
                                  calendarEvent.rflWorkOrder &&
                                  calendarEvent.rflWorkOrder.detailsJson
                                    ? JSON.parse(
                                        calendarEvent.rflWorkOrder.detailsJson,
                                      )
                                    : ""
                                }
                              />
                            </Box>
                          ) : (
                            <Box>
                              <Box>
                                {calendarEvent &&
                                  calendarEvent.rflTasks &&
                                  calendarEvent.rflTasks.map((rflTask) => {
                                    // let progress = 0;
                                    // for (let subphase of phase.subPhases) {
                                    //   progress += subphase.progress;
                                    // }

                                    // progress = progress / phase.subPhases.length;

                                    return (
                                      <Box>
                                        <RFLTaskDialogButton
                                          rflTask={rflTask}
                                          updateTaskProgress={
                                            updateTaskProgress
                                          }
                                          loading={updatingProgress}
                                          errorMessage={updateProgressApiError}
                                        >
                                          <Box
                                            sx={{
                                              display: "flex",
                                              flexDirection: "row",
                                              justifyContent: "center",
                                              alignItems: "center",
                                              padding: "16px",
                                              cursor: "pointer",
                                              "&:hover": {
                                                background: "#eeeeee",
                                              },
                                            }}
                                          >
                                            <Typography sx={{ flex: 1 }}>
                                              {rflTask.name}
                                            </Typography>
                                            <Typography>
                                              {rflTask.progress}/{rflTask.goal}{" "}
                                              {rflTask.uom}
                                            </Typography>
                                          </Box>
                                        </RFLTaskDialogButton>
                                        <Divider />
                                      </Box>
                                    );
                                  })}
                              </Box>
                            </Box>
                          )}
                        </>
                      )}
                    </Box>
                    <Divider sx={{ marginY: "10px" }} />
                    <Typography variant="h6">Documents</Typography>
                    <Box>
                      {calendarEvent.eventEntity == "IND" && (
                        <WorkOrderButton
                          orderId={calendarEvent.order.id}
                          size="small"
                        />
                      )}
                    </Box>
                    <Divider sx={{ marginY: "10px" }} />

                    {/* Shop Notes */}
                    <Box
                      style={{
                        flex: 1,
                        // paddingLeft: 16, paddingRight: 16
                      }}
                    >
                      <Typography
                        sx={{ fontWeight: "bold", overflow: "hidden" }}
                      >
                        Shop Notes (Shop Only):
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "row",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <TextareaAutosize
                          placeholder="Add instructions or end of day progress notes here"
                          style={{
                            marginTop: "8px",
                            // color: "#fff",
                            flex: 1,
                          }}
                          fullWidth
                          minRows={3}
                          value={fabricatorNote}
                          onChange={(e) => {
                            setFabricatorNote(e.target.value);
                          }}
                        />
                        <LoadingButton
                          loading={isLoadingNotes}
                          onClick={async () => {
                            await addShopNote(fabricatorNote, true);
                            setFabricatorNote("");
                          }}
                        >
                          Add
                        </LoadingButton>
                      </Box>

                      <FabricatorNotes shopNotes={shopNotes} />
                    </Box>
                    {decoded.role == "EZORDER_ADMIN" && (
                      <DialogActions>
                        <Button
                          onClick={() => {
                            handleCopyUnfinished(calendarEvent);
                          }}
                        >
                          Reschedule Unfinished Tasks
                        </Button>
                      </DialogActions>
                    )}
                  </DialogContent>
                )}
              </>
            )}
            {/* <DialogActions>
              <Button onClick={handleEdit}>Edit</Button>
            </DialogActions> */}
          </>
        )}
      </Dialog>
    );
};

export default JobDialog;
